import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  CardPurseIcon,
  CardsItemIcon,
  FiservCardInterface,
  FiservCardPurse,
  Hint,
  Inscription,
  List,
  Toggle,
  useResponsive,
} from '@common-fe/common-fe';

import { Roles } from '@/common/constants';
import { useHasAccess } from '@/modules/core/hooks';
import { FEATURE_FLAGS, useFeatureFlag } from '@/utils/featureFlags';

import useCardAccessModalStore from './stores/useCardAccessModal.store';

const CARD_PURSE_ICON_SIZE = '32px';
const LOCKED_BY_HEALTH_PLAN_HINT = 'Account is locked based on plan rules';

interface Props {
  purse?: FiservCardPurse;
  card?: FiservCardInterface;
  withoutToggle?: boolean;
}

export const CardAccessListItem: React.FC<Props> = ({ purse, card, withoutToggle }) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const {
    state: { lockedPursesIds, isCardLocked },
    handleToggleLockedPurse,
    handleToggleCardLock,
  } = useCardAccessModalStore();
  const isLockByHealthPlanEnabled = useFeatureFlag(FEATURE_FLAGS.CARD_PURSE_LOCK_BY_HEALTH_PLAN);
  const isLockedByHealthPlan = useMemo(() => {
    if (!isLockByHealthPlanEnabled) return false;
    return purse?.isLockedByHealthPlan === true;
  }, [purse?.isLockedByHealthPlan, isLockByHealthPlanEnabled]);
  const isEmployeeLimitedAccess = useHasAccess([
    {
      role: Roles.employeeLimitedAccess,
    },
  ]);
  const isLockedMode = useMemo(() => {
    if (card) {
      return isCardLocked;
    }
    const isPurseLocked = purse?.id && lockedPursesIds.includes(purse?.id);
    return isPurseLocked;
  }, [purse, card, lockedPursesIds, isCardLocked]);

  const [isActive, setIsActive] = useState<boolean>(!isLockedMode);

  const handleToggleChange = (value: boolean) => {
    if (isLockedByHealthPlan) return;
    setIsActive(value);
    if (card) {
      handleToggleCardLock(!value);
    } else if (purse?.id) {
      handleToggleLockedPurse(purse.id, !value);
    }
  };

  useEffect(() => {
    if (card) {
      setIsActive(!isCardLocked);
    } else if (purse?.id) {
      const isPurseLocked = lockedPursesIds.includes(purse?.id);
      setIsActive(!isPurseLocked);
    }
  }, [lockedPursesIds, purse?.id, isCardLocked, card]);

  const toggleContent = (
    <Box direction="row" align="center" gap="xs">
      <Toggle value={isActive} twoColorsMode onChange={handleToggleChange} disabled={isLockedByHealthPlan} />
      <Box style={{ width: '55px', whiteSpace: 'nowrap' }}>
        <Inscription weight={500} color="textSecondary" size="small">
          {t(isActive ? 'Unlocked' : 'Locked')}
        </Inscription>
      </Box>
    </Box>
  );

  return (
    <List.Item
      direction="row"
      justify="between"
      height="66px"
      data-testid="card-access-table-element_id"
      border={{ color: isLockedMode && !isLockedByHealthPlan ? 'dangerBorder' : 'border1', size: 'small' }}
      background={isLockedMode && !isLockedByHealthPlan ? 'dangerContainer' : 'canvas'}
      round="button1Round"
      align="center"
      pad={{ horizontal: 'l', vertical: 'm' }}
      style={{ cursor: 'initial' }}
    >
      <Box
        width={{ max: withoutToggle ? '100%' : (isMobile ? '60%' : '420px') }}
        direction="row"
        align="center"
        style={{ minWidth: 0 }}
      >
        {card ? (
          <Box direction="row" align="center" gap="s" width="80%">
            <CardsItemIcon color="iconAccent" size="large" />
            <Inscription weight="bold" color="textBody">
              {isEmployeeLimitedAccess || !card?.last4Number ? '••••' : `•${card?.last4Number}`}
            </Inscription>
          </Box>
        ) : (
          <Box direction="row" align="center" gap="s" style={{ minWidth: 0 }}>
            <CardPurseIcon type={purse?.accountType} size={CARD_PURSE_ICON_SIZE} />
            <Inscription weight="bold" color="textBody" hasEllipsisMode>
              {purse?.accountName}
            </Inscription>
          </Box>
        )}
      </Box>
      {!withoutToggle && (
        <Box direction="row" align="center" width={isMobile ? undefined : '20%'}>
          {isLockedByHealthPlan ? (
            <Hint hintAlign="vertical" hintElement={toggleContent}>
              <Inscription size="12px" textAlign="center">
                {LOCKED_BY_HEALTH_PLAN_HINT}
              </Inscription>
            </Hint>
          ) : (
            toggleContent
          )}
        </Box>
      )}
    </List.Item>
  );
};
